import { Box } from "@mui/material";
import { alpha } from "@mui/material/styles";
import React from "react";

export interface TagProps {
  children: React.ReactNode;
  // "warning" sets a claim apart that asks for care ("likely", "differs",
  // "lacks")
  tone?: "default" | "warning";
  // Slanted, for a mark the pipeline added itself ("likely", "derived")
  italic?: boolean;
  // What the tag means, on hover
  title?: string;
  // The colour of what it names, where a view tells them apart by colour
  // (a maker's part): a dot of it before the words, and a tint behind
  // them; the words stay in the ink of text
  hue?: string;
}

// How strongly a hued tag is tinted
const HUE_TINT = 0.16;

/**
 * A quiet mark beside a value: a stance, a role, a status, a derivation.
 * It is not a control, and so has no border: what has one is a chip that
 * can be clicked, a filter or the sources of a row.
 */
const Tag: React.FC<TagProps> = ({
  children,
  tone = "default",
  italic = false,
  title,
  hue,
}) => (
  <Box
    component="span"
    title={title}
    sx={(theme) => ({
      display: "inline-flex",
      alignItems: "center",
      gap: 0.625,
      px: 0.75,
      borderRadius: `${theme.radius.chip}px`,
      fontSize: "0.72rem",
      lineHeight: "18px",
      whiteSpace: "nowrap",
      verticalAlign: "middle",
      fontStyle: italic ? "italic" : "normal",
      ...(hue
        ? {
            color: theme.palette.text.primary,
            backgroundColor: alpha(hue, HUE_TINT),
          }
        : tone === "warning"
          ? {
              color: theme.palette.warning.main,
              backgroundColor: alpha(theme.palette.warning.main, 0.12),
            }
          : {
              color: theme.palette.text.secondary,
              backgroundColor: alpha(theme.palette.text.primary, 0.08),
            }),
    })}
  >
    {hue && (
      <Box
        component="span"
        aria-hidden
        sx={{ width: 6, height: 6, borderRadius: "50%", backgroundColor: hue, flexShrink: 0 }}
      />
    )}
    {children}
  </Box>
);

export default Tag;
